import { FacilityDetailsResponse } from "@features/ax/assetRegistry/facilities/types";
import { logEvent } from "@shared/analytics/analytics";
import { GenericTable } from "@shared/components/GenericTable";
import { PageHeader } from "@shared/components/PageHeader";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card } from "@shared/ui/card";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Plus, Wrench } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useServiceOrdersList } from "../../../../hooks";
import { ServiceOrderItem } from "../../../../types";

interface OMTabProps {
  facility: FacilityDetailsResponse;
}

export const OMTab: React.FC<OMTabProps> = ({ facility }) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  useEffect(() => {
    logEvent("o&m_tab_viewed");
  }, []);

  const { data: serviceOrdersData } = useServiceOrdersList({
    requestId: facility.id,
    search: search,
    page: currentPage,
  });

  const getServiceTypeFromAssociation = (
    serviceAssociation: string
  ): string => {
    const association = serviceAssociation;

    if (association?.includes("Asset")) return "Asset";
    if (association?.includes("Facility")) return "Facility";
    if (association?.includes("System")) return "System";
    if (association?.includes("Network")) return "Network";

    // Default fallback
    return "Asset";
  };

  const handleViewDetails = (row: any) => {
    const serviceType = getServiceTypeFromAssociation(
      row.sopAssociationTypeDisplay
    );
    const status = row.statusDisplay?.toLowerCase();

    // Determine the route based on status
    let routePrefix = "";

    switch (status) {
      case "created":
      case "overdue":
        routePrefix = "pending";
        break;
      case "assigned":
      case "accepted":
      case "in-progress":
      case "in progress":
        routePrefix = "assigned";
        break;
      case "completed":
      case "rejected":
        routePrefix = "completed";
        break;
      case "canceled":
      case "cancelled":
      case "approved":
        routePrefix = "history";
        break;
      default:
        // Default to pending for unknown statuses
        routePrefix = "pending";
    }

    navigate(
      `/ax/service-order/landing/${routePrefix}/view/${serviceType}/${row.id}`
    );
  };

  const handleSearch = (searchTerm: string) => {
    if (searchTerm.trim()) {
      setSearch(searchTerm);
    } else {
      setSearch("");
    }

    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Pagination logic
  const totalItems = serviceOrdersData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: startIndex + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
    }),
    [currentPage, totalPages, totalItems, startIndex]
  );

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case "high":
      case "3":
        return "bg-red-100 text-red-800 border-red-200";
      case "medium":
      case "2":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "low":
      case "1":
      case "4":
        return "bg-green-100 text-green-800 border-green-200";
      case "undefined":
      case "0":
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "created":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "in progress":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "completed":
        return "bg-green-100 text-green-800 border-green-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-200";
      case "scheduled":
        return "bg-purple-100 text-purple-800 border-purple-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const tableColumns: TableColumn<ServiceOrderItem>[] = [
    {
      key: "jobCardNo",
      header: "Order ID",
      sortable: true,
      headerTooltip: "Unique, system-generated ID",
    },
    {
      key: "sopAssociationTypeDisplay",
      header: "Service Association",
      sortable: true,
      headerTooltip:
        "The category of work or maintenance operation that this work order covers (e.g., Preventive Maintenance, Repair, Inspection, Emergency Response).",
    },
    {
      key: "requestAccountNo",
      header: "Ref ID/Name",
      sortable: true,
      type: "custom",
      render: (value: string, row: ServiceOrderItem) => (
        <div>
          <div>{value}</div>
          <div className="text-sm text-gray-500">
            {row.requestUsername ?? "NA"}
          </div>
        </div>
      ),
      headerTooltip: "The ID and name of the specific entity.",
    },
    {
      key: "priorityDisplay", // Use priorityDisplay first, fallback to priority
      header: "Priority",
      sortable: true,
      type: "custom",
      render: (value: string, row: ServiceOrderItem) => {
        const displayValue = value || row.priority;
        return (
          <Badge className={getPriorityColor(displayValue)}>
            {displayValue}
          </Badge>
        );
      },
      headerTooltip:
        "Indicates the urgency level of the service order (e.g., Low, Medium, High).",
    },
    {
      key: "statusDisplay", // Use statusDisplay first, fallback to status
      header: "Status",
      sortable: true,
      type: "custom",
      render: (value: string, row: ServiceOrderItem) => {
        const displayValue = value || row.status;
        return (
          <Badge className={getStatusColor(displayValue)}>{displayValue}</Badge>
        );
      },
      headerTooltip: "Tag representing the current state",
    },
    {
      key: "sopOperationTypeDisplay", // Use sopOperationTypeDisplay first, fallback to sopOperationType
      header: "Order Type",
      sortable: true,
      type: "custom",
      render: (value: string, row: ServiceOrderItem) => {
        return value || row.sopOperationType || "N/A";
      },
      headerTooltip: "The type of work (e.g., Preventive, Corrective).",
    },
    {
      key: "fieldForceName",
      header: "Assigned To",
      sortable: true,
      headerTooltip: "The name of the technician or team assigned.",
    },
    {
      key: "createdDate",
      header: "Creation Date",
      sortable: true,
      headerTooltip: "The date the order was created.",
    },
    {
      key: "expectedEndDate", // This might not exist in ServiceOrderItem, so it will show empty
      header: "Due Date",
      sortable: true,
      headerTooltip: "The target completion date.",
    },
  ];

  const serviceOrderActions: TableAction[] = [
    {
      label: "View Details",
      onClick: handleViewDetails,
      icon: "Eye",
      variant: "outline",
    },
  ];

  const handleCreateServiceOrder = () => {
    navigate(`/ax/service-order/landing/create`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <Wrench className="h-5 w-5 text-blue-600" />
          </div>
          <PageHeader
            title="Service Orders"
            subtitle={`Monitor and manage service orders for ${facility.facilityName}`}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleCreateServiceOrder}>
            <Plus className="h-4 w-4 mr-2" />
            Create Service Order
          </Button>
        </div>
      </div>

      <Card>
        <GenericTable
          data={serviceOrdersData?.results}
          columns={tableColumns}
          rowKey="id"
          actions={serviceOrderActions}
          actionsType="dropdown"
          emptyMessage="No service orders found for this facility"
          emptyIcon={<Wrench className="h-8 w-8 text-gray-400" />}
          searchPlaceholder="Search by order ID, ref ID, name"
          searchParamName="search"
          searchDebounceMs={300}
          onSearch={handleSearch}
          pagination={pagination}
          onPageChange={handlePageChange}
        />
      </Card>
    </div>
  );
};
